import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Users, Plus, Clock, Loader2, UserPlus, LogIn, Mail, KeyRound, Linkedin, Github, Globe, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import type { FlowResult } from "./ChallengeJoinFlow";

interface TeamMember {
  user_id: string;
  role: string;
  first_name: string | null;
  last_name: string | null;
  linkedin_url: string | null;
  github_url: string | null;
  portfolio_url: string | null;
}

interface Team {
  id: string;
  name: string;
  description: string | null;
  created_by: string;
  status: string;
  member_count: number;
  lead_email: string | null;
  lead_first_name: string | null;
  lead_last_name: string | null;
  has_pending_request: boolean;
  members: TeamMember[];
}

const formatMemberName = (member: { 
  first_name: string | null; 
  last_name: string | null; 
}, fallback?: string): string => {
  if (member.first_name) {
    return member.last_name 
      ? `${member.first_name} ${member.last_name}`
      : member.first_name;
  }
  return fallback || "Team Member";
};

interface TeamSelectionStepProps {
  challengeId: string;
  onTeamSelected: (result: FlowResult) => void;
  initialAction?: "join" | "create";
}

const TeamSelectionStep: React.FC<TeamSelectionStepProps> = ({
  challengeId,
  onTeamSelected,
  initialAction = "join",
}) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(initialAction === "create");
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  
  // Existing team membership (if user is already on a team for this challenge)
  const [existingTeamMembership, setExistingTeamMembership] = useState<{
    teamId: string;
    teamName: string;
    role: string;
  } | null>(null);
  
  // Create form state
  const [teamName, setTeamName] = useState("");
  const [teamDescription, setTeamDescription] = useState("");

  // Login flow state
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginSent, setLoginSent] = useState(false);
  const [loginMode, setLoginMode] = useState<"magic" | "password">("magic");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    type: "join" | "create";
    team?: Team;
  } | null>(null);

  useEffect(() => {
    fetchTeams();
    getCurrentUser();

    // Listen for auth state changes (user logs in via magic link)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === "SIGNED_IN" && session?.user) {
          setCurrentUserId(session.user.id);
          setShowLoginPrompt(false);
          setLoginSent(false);
          
          // Ensure profile exists (safety net)
          setTimeout(async () => {
            try {
              await supabase.rpc('approve_eventbrite_registrant', {
                p_user_id: session.user.id,
                p_email: session.user.email || '',
              });
            } catch (error) {
              console.log("Profile sync in auth change (non-critical):", error);
            }
          }, 0);
          
          // Complete the pending action
          if (pendingAction) {
            if (pendingAction.type === "join" && pendingAction.team) {
              // Small delay to ensure state is updated
              setTimeout(() => {
                handleRequestToJoinAfterLogin(pendingAction.team!, session.user.id);
              }, 100);
            } else if (pendingAction.type === "create") {
              toast.success("Logged in! You can now create your team.");
            }
            setPendingAction(null);
          }
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [challengeId]);

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setCurrentUserId(user?.id || null);
  };

  const handleSendLoginLink = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!loginEmail) {
      toast.error("Please enter your email");
      return;
    }

    setLoginLoading(true);
    try {
      // Store return URL for after auth
      localStorage.setItem("auth_return_url", window.location.pathname + window.location.search);
      
      const { error } = await supabase.auth.signInWithOtp({
        email: loginEmail,
        options: { 
          shouldCreateUser: false,
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      
      if (error) {
        // If user doesn't exist, suggest they register first
        if (error.message.includes("not found") || error.message.includes("not registered") || error.message.includes("Signups not allowed")) {
          toast.error("No account found. Please complete event registration first.");
          return;
        }
        throw error;
      }
      
      setLoginSent(true);
      toast.success("Login link sent to your email!");
    } catch (error: any) {
      toast.error(error.message || "Failed to send login link");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoginLoading(true);
    try {
      // Store return URL for after auth
      localStorage.setItem("auth_return_url", window.location.pathname + window.location.search);
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      
      if (error) throw error;
    } catch (error: any) {
      toast.error(error.message || "Failed to sign in with Google");
      setLoginLoading(false);
    }
  };

  // Helper to ensure profile exists (safety net for all login paths)
  const ensureProfileExists = async (userId: string, email: string) => {
    try {
      await supabase.rpc('approve_eventbrite_registrant', {
        p_user_id: userId,
        p_email: email,
      });
    } catch (error) {
      // Log but don't fail - profile may exist or user may not be from Eventbrite
      console.log("Profile sync (non-critical):", error);
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      toast.error("Please enter email and password");
      return;
    }

    setLoginLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password: loginPassword,
      });

      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          toast.error("Invalid email or password");
        } else {
          throw error;
        }
        return;
      }

      if (data.user) {
        // Ensure profile exists before proceeding
        await ensureProfileExists(data.user.id, loginEmail);
        
        setCurrentUserId(data.user.id);
        setShowLoginPrompt(false);
        toast.success("Logged in successfully!");
        
        // Complete the pending action
        if (pendingAction) {
          if (pendingAction.type === "join" && pendingAction.team) {
            setTimeout(() => {
              handleRequestToJoinAfterLogin(pendingAction.team!, data.user.id);
            }, 100);
          } else if (pendingAction.type === "create") {
            toast.success("You can now create your team.");
          }
          setPendingAction(null);
        }
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to sign in");
    } finally {
      setLoginLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginEmail) {
      toast.error("Please enter your email first");
      return;
    }

    setLoginLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(loginEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      setResetSent(true);
      toast.success("Password reset link sent to your email!");
    } catch (error: any) {
      toast.error(error.message || "Failed to send reset link");
    } finally {
      setLoginLoading(false);
    }
  };

  const fetchTeams = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      // First check if user is already on a team for this challenge
      if (user) {
        const { data: existingMembership } = await supabase
          .from("challenge_team_members")
          .select("team_id, role, challenge_teams(name, deleted_at)")
          .eq("user_id", user.id)
          .eq("challenge_id", challengeId)
          .maybeSingle();
        
        // Check if user is on a team AND the team is not soft-deleted
        const teamData = existingMembership?.challenge_teams as { name: string; deleted_at: string | null } | null;
        const hasActiveTeam = existingMembership?.team_id && !teamData?.deleted_at;
        
        if (hasActiveTeam) {
          // User is already on an active team - set state to auto-skip
          setExistingTeamMembership({
            teamId: existingMembership.team_id,
            teamName: teamData?.name || "Your Team",
            role: existingMembership.role || "member",
          });
          setLoading(false);
          return; // Don't load teams, user already has one
        }
      }

      // Fetch all active (non-deleted) teams for this challenge
      const { data: teamsData, error: teamsError } = await supabase
        .from("challenge_teams")
        .select(`
          id,
          name,
          description,
          created_by,
          status,
          deleted_at
        `)
        .eq("challenge_id", challengeId)
        .eq("status", "active")
        .is("deleted_at", null);

      if (teamsError) throw teamsError;

      // Get member counts for each team
      const teamIds = teamsData?.map(t => t.id) || [];
      
      let memberCounts: Record<string, number> = {};
      let userTeamIds: Set<string> = new Set();
      
      if (teamIds.length > 0) {
        const { data: membersData } = await supabase
          .from("challenge_team_members")
          .select("team_id, user_id")
          .in("team_id", teamIds);
        
        membersData?.forEach(m => {
          memberCounts[m.team_id] = (memberCounts[m.team_id] || 0) + 1;
          // Track teams the current user is already a member of
          if (user && m.user_id === user.id) {
            userTeamIds.add(m.team_id);
          }
        });
      }

      // Get lead emails and names
      const creatorIds = teamsData?.map(t => t.created_by).filter(Boolean) || [];
      let leadEmails: Record<string, string> = {};
      let leadProfiles: Record<string, { first_name: string | null; last_name: string | null }> = {};

      if (creatorIds.length > 0) {
        const { data: profilesData } = await supabase
          .from("profiles")
          .select("id, email")
          .in("id", creatorIds);
        
        profilesData?.forEach(p => {
          if (p.email) leadEmails[p.id] = p.email;
        });

        // Fetch lead names from contributor_profiles
        const { data: contributorData } = await supabase
          .from("contributor_profiles")
          .select("id, first_name, last_name")
          .in("id", creatorIds);
        
        contributorData?.forEach(c => {
          leadProfiles[c.id] = { first_name: c.first_name, last_name: c.last_name };
        });
      }

      // Fetch all team members with their profiles for the "Meet the Team" section
      let teamMembersMap: Record<string, TeamMember[]> = {};
      if (teamIds.length > 0) {
        const { data: allMembersData } = await supabase
          .from("challenge_team_members")
          .select("team_id, user_id, role")
          .in("team_id", teamIds);

        if (allMembersData && allMembersData.length > 0) {
          const memberUserIds = [...new Set(allMembersData.map(m => m.user_id))];
          
          // Fetch contributor profiles for all members
          const { data: memberProfiles } = await supabase
            .from("contributor_profiles")
            .select("id, first_name, last_name, linkedin_url, github_url, portfolio_url")
            .in("id", memberUserIds);
          
          const profilesById: Record<string, typeof memberProfiles extends (infer T)[] | null ? T : never> = {};
          memberProfiles?.forEach(p => {
            profilesById[p.id] = p;
          });

          // Group members by team
          allMembersData.forEach(m => {
            if (!teamMembersMap[m.team_id]) {
              teamMembersMap[m.team_id] = [];
            }
            const profile = profilesById[m.user_id];
            teamMembersMap[m.team_id].push({
              user_id: m.user_id,
              role: m.role || "member",
              first_name: profile?.first_name || null,
              last_name: profile?.last_name || null,
              linkedin_url: profile?.linkedin_url || null,
              github_url: profile?.github_url || null,
              portfolio_url: profile?.portfolio_url || null,
            });
          });

          // Sort members: leads first, then alphabetically by name
          Object.keys(teamMembersMap).forEach(teamId => {
            teamMembersMap[teamId].sort((a, b) => {
              if (a.role === "lead" && b.role !== "lead") return -1;
              if (a.role !== "lead" && b.role === "lead") return 1;
              const nameA = formatMemberName(a);
              const nameB = formatMemberName(b);
              return nameA.localeCompare(nameB);
            });
          });
        }
      }

      // Check for pending requests from current user
      let pendingRequests: Set<string> = new Set();
      if (user && teamIds.length > 0) {
        const { data: requestsData } = await supabase
          .from("team_join_requests")
          .select("team_id")
          .eq("user_id", user.id)
          .eq("status", "pending")
          .in("team_id", teamIds);
        
        requestsData?.forEach(r => pendingRequests.add(r.team_id));
      }

      // Build final teams array, filtering out teams user is already a member of
      const enrichedTeams: Team[] = (teamsData || [])
        .filter(t => !userTeamIds.has(t.id)) // Filter out teams user is already on
        .map(t => ({
          ...t,
          member_count: memberCounts[t.id] || 0,
          lead_email: t.created_by ? leadEmails[t.created_by] || null : null,
          lead_first_name: t.created_by ? leadProfiles[t.created_by]?.first_name || null : null,
          lead_last_name: t.created_by ? leadProfiles[t.created_by]?.last_name || null : null,
          has_pending_request: pendingRequests.has(t.id),
          members: teamMembersMap[t.id] || [],
        }));

      // DEBUG: Temporary logging to diagnose empty "Meet the Team" issue
      console.log("DEBUG: teamMembersMap:", JSON.stringify(teamMembersMap, null, 2));
      console.log("DEBUG: enrichedTeams with members:", enrichedTeams.map(t => ({
        name: t.name,
        id: t.id,
        membersLength: t.members.length,
        members: t.members
      })));

      setTeams(enrichedTeams);
    } catch (error) {
      console.error("Error fetching teams:", error);
      toast.error("Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestToJoinAfterLogin = async (team: Team, userId: string) => {
    setSubmitting(team.id);
    try {
      // Create join request
      const { error: requestError } = await supabase
        .from("team_join_requests")
        .insert({
          team_id: team.id,
          user_id: userId,
          status: "pending",
        });

      if (requestError) throw requestError;

      // Create notification for team lead
      if (team.created_by) {
        const { data: { user } } = await supabase.auth.getUser();
        await supabase
          .from("user_notifications")
          .insert({
            user_id: team.created_by,
            type: "join_request",
            title: "New Join Request",
            message: `${user?.email || "Someone"} wants to join your team "${team.name}"`,
            link: `/dashboard/challenges`,
            metadata: { team_id: team.id, requester_id: userId },
          });
      }

      onTeamSelected({
        action: "requested_join",
        teamId: team.id,
        teamName: team.name,
      });
    } catch (error: any) {
      console.error("Error requesting to join:", error);
      if (error.code === "23505") {
        toast.error("You've already requested to join this team");
      } else {
        toast.error("Failed to send join request");
      }
    } finally {
      setSubmitting(null);
    }
  };

  const handleRequestToJoin = async (team: Team) => {
    if (!currentUserId) {
      // Instead of toast error, show inline login prompt
      setPendingAction({ type: "join", team });
      setShowLoginPrompt(true);
      return;
    }

    await handleRequestToJoinAfterLogin(team, currentUserId);
  };

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUserId) {
      // Instead of toast error, show inline login prompt
      setPendingAction({ type: "create" });
      setShowLoginPrompt(true);
      return;
    }

    if (!teamName.trim()) {
      toast.error("Please enter a team name");
      return;
    }

    setSubmitting("create");
    try {
      // Create the team
      const { data: newTeam, error: teamError } = await supabase
        .from("challenge_teams")
        .insert({
          challenge_id: challengeId,
          name: teamName.trim(),
          description: teamDescription.trim() || null,
          created_by: currentUserId,
          status: "active",
        })
        .select("id, name")
        .single();

      if (teamError) throw teamError;

      // Add creator as team lead member
      const { error: memberError } = await supabase
        .from("challenge_team_members")
        .insert({
          challenge_id: challengeId,
          team_id: newTeam.id,
          user_id: currentUserId,
          role: "lead",
        });

      if (memberError) throw memberError;

      onTeamSelected({
        action: "created_team",
        teamId: newTeam.id,
        teamName: newTeam.name,
      });
    } catch (error) {
      console.error("Error creating team:", error);
      toast.error("Failed to create team");
    } finally {
      setSubmitting(null);
    }
  };

  // Auto-proceed if user is already on a team (must be before any conditional returns)
  useEffect(() => {
    if (existingTeamMembership) {
      onTeamSelected({
        action: "already_on_team",
        teamId: existingTeamMembership.teamId,
        teamName: existingTeamMembership.teamName,
      });
    }
  }, [existingTeamMembership, onTeamSelected]);

  // If user is already on a team, show loading state while auto-proceeding
  if (existingTeamMembership) {
    return (
      <div className="text-center py-8 space-y-3">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
        <p className="text-muted-foreground">
          You're already on team "{existingTeamMembership.teamName}"
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  // Inline login prompt UI
  if (showLoginPrompt) {
    return (
      <Card className="p-6">
        <div className="space-y-4">
          <div className="text-center space-y-2">
            <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <LogIn className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-lg font-semibold">Sign in to continue</h3>
            <p className="text-sm text-muted-foreground">
              {pendingAction?.type === "join" 
                ? `Sign in to request to join "${pendingAction.team?.name}"`
                : "Sign in to create your team"}
            </p>
          </div>

          {showResetPassword ? (
            // Password Reset UI
            <div className="space-y-4">
              {!resetSent ? (
                <form onSubmit={handlePasswordReset} className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Enter your email and we'll send you a link to reset your password.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">Email</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder="you@example.com"
                      autoFocus
                    />
                  </div>
                  
                  <Button type="submit" className="w-full" disabled={loginLoading}>
                    {loginLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Send Reset Link"
                    )}
                  </Button>
                  
                  <p className="text-xs text-center text-muted-foreground">
                    Don't see the email? Check your spam or junk folder.
                  </p>
                  
                  <Button 
                    type="button" 
                    variant="ghost" 
                    className="w-full"
                    onClick={() => setShowResetPassword(false)}
                  >
                    Back to login
                  </Button>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="bg-muted/50 rounded-lg p-4 text-center">
                    <Mail className="h-8 w-8 text-primary mx-auto mb-2" />
                    <p className="text-sm">
                      Reset link sent to <strong>{loginEmail}</strong>
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Click the link in your email to set a new password.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Don't see it? Check your spam or junk folder.
                    </p>
                  </div>
                  
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => {
                      setShowResetPassword(false);
                      setResetSent(false);
                    }}
                  >
                    Back to login
                  </Button>
                </div>
              )}
            </div>
          ) : !loginSent ? (
            <div className="space-y-4">
              {/* Google Sign-In */}
              <Button 
                type="button" 
                variant="outline" 
                className="w-full"
                onClick={handleGoogleSignIn}
                disabled={loginLoading}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Or
                  </span>
                </div>
              </div>

              <Tabs value={loginMode} onValueChange={(v) => setLoginMode(v as "magic" | "password")} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="magic" className="text-xs">
                    <Mail className="h-3 w-3 mr-1" />
                    Email Link
                  </TabsTrigger>
                  <TabsTrigger value="password" className="text-xs">
                    <KeyRound className="h-3 w-3 mr-1" />
                    Password
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="magic" className="space-y-4 mt-4">
                  <form onSubmit={handleSendLoginLink} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email-magic">Email</Label>
                      <Input
                        id="login-email-magic"
                        type="email"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        placeholder="you@example.com"
                      />
                    </div>
                    
                    <Button type="submit" className="w-full" disabled={loginLoading}>
                      {loginLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        "Send Login Link"
                      )}
                    </Button>
                    
                    <p className="text-xs text-center text-muted-foreground">
                      Don't see the email? Check your spam or junk folder.
                    </p>
                  </form>
                </TabsContent>
                
                <TabsContent value="password" className="space-y-4 mt-4">
                  <form onSubmit={handlePasswordLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email-pass">Email</Label>
                      <Input
                        id="login-email-pass"
                        type="email"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        placeholder="you@example.com"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <Input
                        id="login-password"
                        type="password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        placeholder="••••••••"
                      />
                    </div>
                    
                    <Button type="submit" className="w-full" disabled={loginLoading}>
                      {loginLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Signing in...
                        </>
                      ) : (
                        "Sign In"
                      )}
                    </Button>
                    
                    <button
                      type="button"
                      onClick={() => setShowResetPassword(true)}
                      className="text-xs text-primary hover:underline w-full text-center"
                    >
                      Forgot password? Set or reset it here
                    </button>
                  </form>
                </TabsContent>
              </Tabs>
              
              <Button 
                type="button" 
                variant="ghost" 
                className="w-full"
                onClick={() => {
                  setShowLoginPrompt(false);
                  setPendingAction(null);
                }}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4 text-center">
                <Mail className="h-8 w-8 text-primary mx-auto mb-2" />
                <p className="text-sm">
                  Login link sent to <strong>{loginEmail}</strong>
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Click the link in your email to sign in. This window will update automatically.
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Don't see it? Check your spam or junk folder.
                </p>
              </div>
              
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  setLoginSent(false);
                  setLoginEmail("");
                }}
              >
                Use a different email
              </Button>
            </div>
          )}
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Existing Teams Section */}
      {!showCreateForm && teams.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Join an Existing Team
          </h3>
          
          <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
            {teams.map((team) => (
              <Card key={team.id} className="hover:border-primary/50 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-foreground truncate">
                        {team.name}
                      </h4>
                      {team.description && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {team.description}
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {team.member_count} member{team.member_count !== 1 ? "s" : ""}
                        </span>
                        {(team.lead_first_name || team.lead_email) && (
                          <span className="truncate">
                            Led by {team.lead_first_name 
                              ? `${team.lead_first_name}${team.lead_last_name ? ` ${team.lead_last_name}` : ''}`
                              : team.lead_email?.split("@")[0]}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex-shrink-0">
                      {team.has_pending_request ? (
                        <Badge variant="secondary" className="gap-1">
                          <Clock className="h-3 w-3" />
                          Pending
                        </Badge>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => handleRequestToJoin(team)}
                          disabled={submitting === team.id}
                        >
                          {submitting === team.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <UserPlus className="h-4 w-4 mr-1" />
                              Request to Join
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>

                {/* Meet the Team - Collapsible Section */}
                {team.members.length > 0 && (
                  <Collapsible>
                    <CollapsibleTrigger className="flex items-center gap-2 w-full px-4 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors border-t group">
                      <Users className="h-3 w-3" />
                      <span>Meet the Team</span>
                      <ChevronDown className="h-3 w-3 ml-auto transition-transform group-data-[state=open]:rotate-180" />
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="px-4 pb-4 pt-2 space-y-2 border-t bg-muted/30">
                        {team.members.map((member) => {
                          const hasAnySocial = member.linkedin_url || member.github_url || member.portfolio_url;
                          return (
                            <div 
                              key={member.user_id} 
                              className="flex items-center justify-between gap-2 text-sm"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="truncate">
                                  {formatMemberName(member, "Team Member")}
                                </span>
                                {member.role === "lead" && (
                                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                    Lead
                                  </Badge>
                                )}
                              </div>
                              {hasAnySocial && (
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {member.linkedin_url && (
                                    <a
                                      href={member.linkedin_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="p-1 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                                      title="LinkedIn"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Linkedin className="h-4 w-4" />
                                    </a>
                                  )}
                                  {member.github_url && (
                                    <a
                                      href={member.github_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="p-1 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                                      title="GitHub"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Github className="h-4 w-4" />
                                    </a>
                                  )}
                                  {member.portfolio_url && (
                                    <a
                                      href={member.portfolio_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="p-1 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                                      title="Portfolio"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Globe className="h-4 w-4" />
                                    </a>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Divider */}
      {!showCreateForm && teams.length > 0 && (
        <div className="relative">
          <Separator />
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-3 text-sm text-muted-foreground">
            OR
          </span>
        </div>
      )}

      {/* Create Team Section */}
      {showCreateForm ? (
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary" />
              Create Your Own Team
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateTeam} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="teamName">Team Name *</Label>
                <Input
                  id="teamName"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="e.g., Alpha Builders"
                  className="h-11"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="teamDescription">Description (Optional)</Label>
                <Textarea
                  id="teamDescription"
                  value={teamDescription}
                  onChange={(e) => setTeamDescription(e.target.value)}
                  placeholder="What is your team's approach or focus?"
                  rows={3}
                  className="resize-none"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={submitting === "create"}
                  className="flex-1"
                >
                  {submitting === "create" ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      Create Team & Lead It
                    </>
                  )}
                </Button>
                {teams.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateForm(false)}
                  >
                    Browse Teams
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      ) : (
        <Button
          variant="outline"
          className="w-full h-14"
          onClick={() => setShowCreateForm(true)}
        >
          <Plus className="mr-2 h-5 w-5" />
          Start Your Own Team
        </Button>
      )}

      {/* No teams yet */}
      {!loading && teams.length === 0 && !showCreateForm && (
        <div className="text-center py-8">
          <Users className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
          <h3 className="font-medium text-foreground mb-2">No teams yet</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Be the first to create a team for this challenge!
          </p>
          <Button onClick={() => setShowCreateForm(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create a Team
          </Button>
        </div>
      )}
    </div>
  );
};

export default TeamSelectionStep;
